import {
  normalizeEventUuidList,
  normalizePartLabel,
  normalizeProfileEventPartAssignments,
} from './memberEventPartUtils';
import {
  PARTICIPANT_STATUSES,
  buildParticipantEventMembershipLabel,
  pickFirstString,
  toNum,
} from './memberListUtils';
import { resolveGroupArtistPartValues } from './groupArtistPartUtils';
import { resolveAllPerformancesPartBucket } from './eventParticipantUtils';
import {
  compareEventMemberRows,
  getMemberPartMeta,
  toMemberRowOrder,
} from './memberModalRowSorting';

export const buildGroupEventOrderMap = (groupEvents = []) => {
  const map = new Map();
  groupEvents.forEach((event, index) => {
    const eventUuid = String(event?.event_uuid || '').trim();
    if (!eventUuid || map.has(eventUuid)) return;
    map.set(eventUuid, index);
  });
  return map;
};

export const buildGroupEventMap = (groupEvents = []) => {
  const map = new Map();
  groupEvents.forEach((event) => {
    const eventUuid = String(event?.event_uuid || '').trim();
    if (!eventUuid || map.has(eventUuid)) return;
    map.set(eventUuid, event);
  });
  return map;
};

export const buildMemberPartScopeTabs = (groupEvents = []) => {
  if (!Array.isArray(groupEvents) || groupEvents.length <= 1) return [];
  return groupEvents.map((event) => ({
    eventUuid: String(event?.event_uuid || ''),
    label: pickFirstString(event?.performance_type, event?.name) || '公演未設定',
  }));
};

const normalizeEventScopeTime = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '00:00';
  return raw.length >= 5 ? raw.slice(0, 5) : raw;
};

const toEventScopeTimestamp = (event) => {
  const eventDate = String(event?.event_date || '').trim();
  if (!eventDate) return Number.POSITIVE_INFINITY;
  const hhmm = normalizeEventScopeTime(event?.start_time || event?.open_time);
  const timestamp = Date.parse(`${eventDate}T${hhmm}:00`);
  return Number.isFinite(timestamp) ? timestamp : Number.POSITIVE_INFINITY;
};

const sortGroupEventsForScopeDefault = (groupEvents = []) =>
  (Array.isArray(groupEvents) ? groupEvents : [])
    .slice()
    .sort((a, b) => {
      const timeDiff = toEventScopeTimestamp(a) - toEventScopeTimestamp(b);
      if (timeDiff !== 0) return timeDiff;
      if (a?.is_primary === true && b?.is_primary !== true) return -1;
      if (a?.is_primary !== true && b?.is_primary === true) return 1;
      return String(a?.event_uuid || '').localeCompare(String(b?.event_uuid || ''), 'en');
    });

export const buildPartsByIdMap = (parts = []) => {
  const map = new Map();
  (Array.isArray(parts) ? parts : []).forEach((part) => {
    const numericKeys = [
      toNum(part?.id),
      toNum(part?.value),
      toNum(part?.part_id),
    ].filter(Number.isFinite);
    numericKeys.forEach((key) => {
      if (!map.has(key)) map.set(key, part);
      const stringKey = String(key).trim();
      if (stringKey && !map.has(stringKey)) map.set(stringKey, part);
    });

    [
      part?.value,
      part?.id,
      part?.part_id,
      part?.short_name,
      part?.shortName,
      part?.label,
      part?.name,
    ]
      .map((value) => String(value || '').trim())
      .filter(Boolean)
      .forEach((key) => {
        if (!map.has(key)) map.set(key, part);
      });
  });
  return map;
};

export const buildSortedCastingInfo = (castingInfo = []) =>
  (Array.isArray(castingInfo) ? castingInfo : [])
    .slice()
    .sort((a, b) => (a?.sort_order ?? 9999) - (b?.sort_order ?? 9999));

export const filterParticipatingArtists = (progress = []) =>
  (Array.isArray(progress) ? progress : []).filter((artist) =>
    PARTICIPANT_STATUSES.has(String(artist?.contract_status || '').toLowerCase())
  );

export const resolveNextMemberPartScopeEventUuid = ({
  previousValue,
  groupEvents = [],
  fallbackEventId = '',
}) => {
  if (groupEvents.length > 1) {
    const exists = groupEvents.some(
      (event) => String(event?.event_uuid || '') === String(previousValue)
    );
    if (exists) return String(previousValue || '');

    const currentEvent = groupEvents.find(
      (event) => String(event?.event_uuid || '') === String(fallbackEventId)
    );
    if (currentEvent?.event_uuid) return String(currentEvent.event_uuid);

    const sortedEvents = sortGroupEventsForScopeDefault(groupEvents);
    const now = Date.now();
    const nextUpcomingEvent = sortedEvents.find(
      (event) => toEventScopeTimestamp(event) >= now
    );
    if (nextUpcomingEvent?.event_uuid) {
      return String(nextUpcomingEvent.event_uuid);
    }

    const primaryEvent = sortedEvents.find((event) => event?.is_primary === true);
    const lastEvent = sortedEvents[sortedEvents.length - 1];
    return String(primaryEvent?.event_uuid || lastEvent?.event_uuid || '');
  }
  const singleEventUuid = String(groupEvents?.[0]?.event_uuid || fallbackEventId || '').trim();
  return singleEventUuid || 'all';
};

export const buildSnapshotArtistMap = (participantSnapshot) => {
  const map = new Map();
  const artists = Array.isArray(participantSnapshot?.artists) ? participantSnapshot.artists : [];
  artists.forEach((artist) => {
    const artistId = artist?.artist_id ?? artist?.id;
    if (artistId == null) return;
    map.set(String(artistId), artist);
  });
  return map;
};

export const summarizeArtistEventPartForMemberModal = ({
  artist,
  snapshotArtistMap = new Map(),
  groupEventOrderMap = new Map(),
  groupEventMap = new Map(),
  fallbackPartLabel = '',
  isUnifiedEventGroupChat = false,
}) => {
  const fallback = fallbackPartLabel || 'パート未設定';
  if (!artist) return { partLabel: fallback, eventPartLabel: '', isMixed: false };
  if (!isUnifiedEventGroupChat) return { partLabel: fallback, eventPartLabel: '', isMixed: false };

  const artistId = artist?.artist_id ?? artist?.id;
  if (artistId == null) return { partLabel: fallback, eventPartLabel: '', isMixed: false };

  const snapshotArtist = snapshotArtistMap.get(String(artistId));
  if (!snapshotArtist) return { partLabel: fallback, eventPartLabel: '', isMixed: false };

  const targetEventUuids = normalizeEventUuidList(snapshotArtist?.event_uuids)
    .filter((eventUuid) => groupEventOrderMap.has(eventUuid));
  if (!targetEventUuids.length) return { partLabel: fallback, eventPartLabel: '', isMixed: false };

  const sortedEventUuids = [...targetEventUuids].sort((a, b) => {
    const orderA = groupEventOrderMap.get(a);
    const orderB = groupEventOrderMap.get(b);
    if (orderA !== orderB) return orderA - orderB;
    return String(a).localeCompare(String(b), 'en');
  });

  const assignmentMap = new Map();
  normalizeProfileEventPartAssignments(
    snapshotArtist?.event_part_assignments
    || snapshotArtist?.eventPartAssignments
    || artist?.event_part_assignments
    || artist?.eventPartAssignments
  ).forEach((assignment) => {
    assignmentMap.set(assignment.eventUuid, {
      partLabel: assignment.partLabel,
      hasAssignment: assignment.hasAssignment,
    });
  });

  const entries = sortedEventUuids.map((eventUuid) => {
    const eventInfo = groupEventMap.get(eventUuid);
    const scope = pickFirstString(eventInfo?.performance_type, eventInfo?.name) || eventUuid;
    const assignment = assignmentMap.get(eventUuid);
    const partLabel = normalizePartLabel(assignment?.partLabel);
    const hasAssignment = assignment?.hasAssignment === true;
    return {
      scopeLabel: `@${scope}`,
      partLabel: partLabel || fallback,
      hasAssignment,
    };
  });

  if (entries.length === 1) {
    const only = entries[0];
    return {
      partLabel: only.hasAssignment ? only.partLabel : fallback,
      eventPartLabel: '',
      isMixed: false,
    };
  }

  const configuredPartLabels = Array.from(
    new Set(
      entries
        .map((entry) => entry.partLabel)
        .filter(Boolean)
    )
  );
  const isMixed = configuredPartLabels.length > 1;
  const partLabel = isMixed
    ? fallback
    : (configuredPartLabels[0] || fallback);
  const eventPartLabel = isMixed
    ? entries.map((entry) => `${entry.scopeLabel}: ${entry.partLabel}`).join(', ')
    : '';
  return {
    partLabel,
    eventPartLabel,
    isMixed,
  };
};

export const buildParticipantMembershipLabelMap = ({
  participants = [],
  keyFields = [],
  groupEvents = [],
  artistsVisibleOnly = false,
}) => {
  const map = new Map();
  participants.forEach((participant) => {
    const label = buildParticipantEventMembershipLabel(
      participant?.event_uuids,
      groupEvents,
      { artistsVisibleOnly }
    );
    if (!label) return;
    keyFields
      .map((field) => participant?.[field])
      .map((value) => (value == null ? '' : String(value).trim()))
      .filter(Boolean)
      .forEach((key) => {
        if (!map.has(key)) map.set(key, label);
      });
  });
  return map;
};

export const resolveMembershipLabelByFields = ({
  item,
  keyFields = [],
  labelMap = new Map(),
}) => {
  if (!item || !(labelMap instanceof Map)) return '';
  const keys = keyFields
    .map((field) => item?.[field])
    .map((value) => (value == null ? '' : String(value).trim()))
    .filter(Boolean);
  for (const key of keys) {
    const found = labelMap.get(key);
    if (found) return found;
  }
  return '';
};

export const shouldHideArtistEventMembershipLabel = (artist) => {
  if (!artist || typeof artist !== 'object') return false;
  const accessSource = String(
    artist?.chat_access_source || artist?.access_source || ''
  ).trim().toLowerCase();
  const hasAnyContract = artist?.has_any_contract === true;
  const manualEnabled = artist?.manual_enabled === true;
  return !hasAnyContract && (manualEnabled || accessSource === 'manual');
};

export const buildMemberModalMembershipState = ({
  participantSnapshot = null,
  groupEvents = [],
  artistsVisibleOnly = false,
}) => {
  const adminMembershipLabelMap = buildParticipantMembershipLabelMap({
    participants: Array.isArray(participantSnapshot?.admins) ? participantSnapshot.admins : [],
    keyFields: ['admin_user_id', 'id', 'user_id', 'username'],
    groupEvents,
    artistsVisibleOnly,
  });

  const conciergeMembershipLabelMap = buildParticipantMembershipLabelMap({
    participants: Array.isArray(participantSnapshot?.concierge_planners)
      ? participantSnapshot.concierge_planners
      : [],
    keyFields: ['artist_id', 'id'],
    groupEvents,
    artistsVisibleOnly,
  });

  const artistMembershipLabelMap = buildParticipantMembershipLabelMap({
    participants: [
      ...(Array.isArray(participantSnapshot?.artists) ? participantSnapshot.artists : []),
      ...(Array.isArray(participantSnapshot?.contracted_no_access_artists)
        ? participantSnapshot.contracted_no_access_artists
        : []),
      ...(Array.isArray(participantSnapshot?.manual_chat_artists)
        ? participantSnapshot.manual_chat_artists
        : []),
    ].filter((artist) => !shouldHideArtistEventMembershipLabel(artist)),
    keyFields: ['artist_id', 'id'],
    groupEvents,
    artistsVisibleOnly,
  });

  return {
    adminMembershipLabelMap,
    conciergeMembershipLabelMap,
    artistMembershipLabelMap,
    resolveAdminMembershipLabel: (admin) =>
      resolveMembershipLabelByFields({
        item: admin,
        keyFields: ['admin_user_id', 'id', 'user_id', 'username'],
        labelMap: adminMembershipLabelMap,
      }),
    resolveConciergeMembershipLabel: (planner) =>
      resolveMembershipLabelByFields({
        item: planner,
        keyFields: ['artist_id', 'id'],
        labelMap: conciergeMembershipLabelMap,
      }),
    resolveArtistMembershipLabel: (artist) =>
      resolveMembershipLabelByFields({
        item: artist,
        keyFields: ['artist_id', 'id'],
        labelMap: artistMembershipLabelMap,
      }),
  };
};

export const resolveEventResponsibleAdminsForModal = ({
  participantSnapshot = null,
  assignedAdmins = [],
}) => {
  const snapshotAdmins = Array.isArray(participantSnapshot?.admins)
    ? participantSnapshot.admins
    : [];
  const fallbackAdmins = Array.isArray(assignedAdmins) ? assignedAdmins : [];
  const ordered = [...snapshotAdmins, ...fallbackAdmins];
  const merged = new Map();

  ordered.forEach((admin, index) => {
    if (!admin || typeof admin !== 'object') return;
    const rawKey =
      admin?.admin_user_id ??
      admin?.id ??
      admin?.user_id ??
      admin?.username ??
      admin?.display_name ??
      `idx-${index}`;
    const key = String(rawKey || '').trim();
    if (!key) return;
    if (merged.has(key)) {
      const existing = merged.get(key);
      merged.set(key, { ...admin, ...existing });
      return;
    }
    merged.set(key, admin);
  });

  return Array.from(merged.values());
};

const sortRowsWithContract = (rows = []) => {
  rows.sort(compareEventMemberRows);
};

const sortRowsSimple = (rows = []) => {
  rows.sort((x, y) => {
    const ao = toMemberRowOrder(x?.sortOrder);
    const bo = toMemberRowOrder(y?.sortOrder);
    if (ao !== bo) return ao - bo;
    return String(x?.name || '').localeCompare(String(y?.name || ''), 'ja');
  });
};

export const buildEventMemberSectionsForModal = ({
  artists = [],
  includedArtistIds = null,
  memberPartScopeEventUuid = 'all',
  partsById = new Map(),
  sortedCasting = [],
  snapshotArtistMap = new Map(),
  groupEventsForMembership = [],
  artistMembershipLabelMap = new Map(),
  buildArtistEventPartSummary,
  parsePartIds,
  pickFirstStringFn = pickFirstString,
  useSnapshotArtistsForUnified = false,
  isUnifiedEventGroupChat = false,
}) => {
  const isAllScope = String(memberPartScopeEventUuid) === 'all';
  const sectionMap = new Map();
  const undecided = [];
  const castingSortMap = new Map(
    (Array.isArray(sortedCasting) ? sortedCasting : []).map((ci) => [
      String(ci?.id),
      toMemberRowOrder(ci?.sort_order),
    ])
  );

  (Array.isArray(artists) ? artists : []).forEach((artist) => {
    const artistId = artist?.artist_id ?? artist?.id;
    if (
      includedArtistIds instanceof Set
      && (artistId == null || !includedArtistIds.has(String(artistId)))
    ) {
      return;
    }
    const snapshotArtist =
      artistId != null ? snapshotArtistMap.get(String(artistId)) : null;
    const mainIds = parsePartIds?.(artist?.artist_parts || artist?.parts) || [];
    const mainVal = mainIds[0] ?? null;
    const mainMeta = getMemberPartMeta(partsById, mainVal);
    const profilePartSort = toMemberRowOrder(mainMeta?.sort_order);
    const artistEventUuids = normalizeEventUuidList(
      snapshotArtist?.event_uuids || artist?.event_uuids || artist?.eventUuids
    );

    let scopedPartLabel = '';
    let scopedSortOrder = profilePartSort;
    let scopedHasAssignment = false;
    const normalizedAssignments = normalizeProfileEventPartAssignments(
      snapshotArtist?.event_part_assignments
      || snapshotArtist?.eventPartAssignments
      || artist?.event_part_assignments
      || artist?.eventPartAssignments
    );
    if (!isAllScope && snapshotArtist) {
      if (useSnapshotArtistsForUnified) {
        const inScope = artistEventUuids.includes(String(memberPartScopeEventUuid));
        if (!inScope) return;
      }

      const scoped = normalizedAssignments.find(
        (assignment) => assignment.eventUuid === String(memberPartScopeEventUuid)
      ) || null;
      const label = normalizePartLabel(scoped?.partLabel);
      const fallbackScopedLabel =
        normalizePartLabel(mainMeta?.label)
        || normalizePartLabel(mainMeta?.short_name)
        || '未設定';
      scopedHasAssignment = scoped?.hasAssignment === true || Boolean(label);
      scopedPartLabel = scopedHasAssignment ? (label || fallbackScopedLabel) : fallbackScopedLabel;

      if (Number.isFinite(scoped?.partSortOrder)) {
        scopedSortOrder = scoped.partSortOrder;
      } else if (scoped?.castingInfoId != null) {
        const foundOrder = castingSortMap.get(String(scoped.castingInfoId));
        if (Number.isFinite(foundOrder)) scopedSortOrder = foundOrder;
      }
    }

    const eventMembershipLabel = shouldHideArtistEventMembershipLabel(artist)
      ? ''
      : resolveMembershipLabelByFields({
          item: artist,
          keyFields: ['artist_id', 'id'],
          labelMap: artistMembershipLabelMap,
        });

    const partSummary = typeof buildArtistEventPartSummary === 'function'
      ? buildArtistEventPartSummary(snapshotArtist || artist, mainMeta?.label || '')
      : null;
    const allPerformancesPartBucket = isAllScope
      ? resolveAllPerformancesPartBucket({
          partSummary,
          fallbackPartLabel: mainMeta?.label || '',
          fallbackOrder: profilePartSort,
          mixedOrder: 10000,
          resolveCommonPartOrder: ({ partLabel }) => {
            const matched = normalizedAssignments.find((assignment) => {
              if (!assignment?.partLabel || assignment.hasAssignment !== true) return false;
              return assignment.partLabel === partLabel;
            });
            if (Number.isFinite(matched?.partSortOrder)) {
              return matched.partSortOrder;
            }
            if (matched?.castingInfoId != null) {
              const foundOrder = castingSortMap.get(String(matched.castingInfoId));
              if (Number.isFinite(foundOrder)) return foundOrder;
            }
            return null;
          },
        })
      : null;

    const row = {
      id: artist?.artist_id || artist?.id,
      artist_id: artist?.artist_id || artist?.id,
      name:
        pickFirstStringFn(
          artist?.artist_name,
          artist?.name,
          artist?.artist_name_en,
          artist?.name_en
        ) || 'アーティスト',
      name_en: artist?.artist_name_en || artist?.name_en || '',
      pic: artist?.artist_profile_picture || artist?.profile_picture || null,
      gender: artist?.artist_gender || artist?.gender || null,
      mainPart: mainMeta?.short_name || mainMeta?.label || '',
      mainPartLabel: mainMeta?.label || '',
      isChief: !!artist?.is_chief,
      isConcertmaster: !!artist?.is_concertmaster,
      customRoles: Array.isArray(artist?.custom_roles)
        ? artist.custom_roles
        : Array.isArray(artist?.customRoles)
          ? artist.customRoles
          : [],
      contractTs: (
        artist?.contractTs
        ?? (() => {
          const parsed = Date.parse(artist?.contract_updated_at || '');
          return Number.isFinite(parsed) ? parsed : null;
        })()
      ),
      profile_one_liner: artist?.profile_one_liner || '',
      instagram_url: artist?.instagram_url || '',
      twitter_url: artist?.twitter_url || '',
      facebook_url: artist?.facebook_url || '',
      hp_url: artist?.hp_url || artist?.homepage_url || '',
      career_timeline: Array.isArray(artist?.career_timeline)
        ? artist.career_timeline
        : (Array.isArray(artist?.careerTimeline) ? artist.careerTimeline : []),
      short: isAllScope ? '' : (scopedHasAssignment && scopedPartLabel !== '未設定' ? scopedPartLabel : ''),
      partLabel: isAllScope ? '' : scopedPartLabel,
      sortOrder: isAllScope
        ? (allPerformancesPartBucket?.sectionOrder ?? profilePartSort)
        : scopedSortOrder,
      mainSort: profilePartSort,
      eventMembershipLabel,
      eventPartLabel: isAllScope ? (allPerformancesPartBucket?.eventPartLabel || '') : '',
      event_uuids: artistEventUuids,
      event_part_assignments: Array.isArray(snapshotArtist?.event_part_assignments)
        ? snapshotArtist.event_part_assignments
        : Array.isArray(snapshotArtist?.eventPartAssignments)
          ? snapshotArtist.eventPartAssignments
          : Array.isArray(artist?.event_part_assignments)
            ? artist.event_part_assignments
            : Array.isArray(artist?.eventPartAssignments)
              ? artist.eventPartAssignments
              : [],
      chat_access_source:
        snapshotArtist?.chat_access_source || artist?.chat_access_source || artist?.access_source || 'none',
      chat_access_control_mode:
        snapshotArtist?.chat_access_control_mode || artist?.chat_access_control_mode || artist?.access_control_mode || 'auto',
      has_any_contract:
        snapshotArtist?.has_any_contract === true || artist?.has_any_contract === true,
      has_auto_chat_access:
        snapshotArtist?.has_auto_chat_access === true || artist?.has_auto_chat_access === true,
      auto_chat_access_source:
        snapshotArtist?.auto_chat_access_source || artist?.auto_chat_access_source || 'none',
      has_contract_access:
        snapshotArtist?.has_contract_access === true || artist?.has_contract_access === true,
      manual_enabled:
        snapshotArtist?.manual_enabled === true || artist?.manual_enabled === true,
      has_manual_override:
        snapshotArtist?.has_manual_override === true || artist?.has_manual_override === true,
      manual_override_mode:
        snapshotArtist?.manual_override_mode || artist?.manual_override_mode || null,
      group_events: groupEventsForMembership,
    };
    row.isUnifiedEventGroupChat = isUnifiedEventGroupChat;

    if (isAllScope) {
      const sectionKey = allPerformancesPartBucket?.sectionLabel || mainMeta?.label || '未設定';
      if (!sectionMap.has(sectionKey)) {
        sectionMap.set(sectionKey, {
          id: sectionKey,
          title: sectionKey,
          titleShort: '',
          order: allPerformancesPartBucket?.sectionOrder ?? profilePartSort,
          members: [],
        });
      }
      const section = sectionMap.get(sectionKey);
      section.order = Math.min(
        toMemberRowOrder(section.order),
        toMemberRowOrder(allPerformancesPartBucket?.sectionOrder, profilePartSort)
      );
      section.members.push(row);
      return;
    }

    if (scopedPartLabel && scopedPartLabel !== '未設定') {
      const sectionKey = scopedPartLabel;
      if (!sectionMap.has(sectionKey)) {
        sectionMap.set(sectionKey, {
          id: sectionKey,
          title: scopedPartLabel,
          titleShort: '',
          order: scopedSortOrder,
          members: [],
        });
      }
      const section = sectionMap.get(sectionKey);
      section.order = Math.min(section.order, scopedSortOrder);
      section.members.push(row);
    } else {
      undecided.push(row);
    }
  });

  if (isAllScope) {
    const sections = Array.from(sectionMap.values()).sort((a, b) => {
      const ao = toMemberRowOrder(a?.order);
      const bo = toMemberRowOrder(b?.order);
      if (ao !== bo) return ao - bo;
      return String(a?.title || '').localeCompare(String(b?.title || ''), 'ja');
    });
    sections.forEach((section) => sortRowsWithContract(section.members));
    return {
      decidedSections: sections.filter((section) => section.members.length > 0),
      undecidedList: [],
    };
  }

  const sections = Array.from(sectionMap.values()).sort((a, b) => {
    const ao = toMemberRowOrder(a?.order);
    const bo = toMemberRowOrder(b?.order);
    if (ao !== bo) return ao - bo;
    return String(a?.title || '').localeCompare(String(b?.title || ''), 'ja');
  });
  sections.forEach((section) => sortRowsWithContract(section.members));
  sortRowsSimple(undecided);

  return {
    decidedSections: sections.filter((section) => section.members.length > 0),
    undecidedList: undecided,
  };
};

export const buildGroupArtistSectionsForModal = ({
  artists = [],
  partsById = new Map(),
  parsePartIds,
  pickFirstStringFn = pickFirstString,
  resolvePartIdsFn = null,
}) => {
  const sectionMap = new Map();
  (Array.isArray(artists) ? artists : []).forEach((artist, index) => {
    const eventPartLabel = pickFirstStringFn(
      artist?.event_part_label,
      artist?.eventPartLabel,
    );
    const hasEventPartAssignment =
      artist?.has_event_part_assignment === true
      || artist?.hasEventPartAssignment === true
      || Boolean(eventPartLabel);
    const partIds = typeof resolvePartIdsFn === 'function'
      ? (resolvePartIdsFn(artist) || [])
      : (() => {
          const parsed = parsePartIds?.(artist?.artist_parts || artist?.parts) || [];
          return parsed.length > 0 ? parsed : resolveGroupArtistPartValues(artist);
        })();
    const mainPartId = partIds[0] ?? null;
    const partMeta = getMemberPartMeta(partsById, mainPartId);
    const eventCastingInfoId = artist?.casting_info_id ?? artist?.castingInfoId ?? null;
    const sectionId = hasEventPartAssignment
      ? `event:${eventCastingInfoId ?? (eventPartLabel || '__unassigned__')}`
      : (mainPartId != null ? String(mainPartId) : '__unassigned__');
    const fallbackLabel = pickFirstStringFn(
      artist?.part_label,
      artist?.main_part_label,
      artist?.partLabel,
      artist?.mainPartLabel,
      artist?.part_short_name,
      artist?.main_part_short_name,
      artist?.partShortName,
      artist?.mainPartShortName,
    );
    const fallbackShort = pickFirstStringFn(
      artist?.part_short_name,
      artist?.main_part_short_name,
      artist?.partShortName,
      artist?.mainPartShortName,
      artist?.part_label,
      artist?.main_part_label,
      artist?.partLabel,
      artist?.mainPartLabel,
    );
    const sectionTitle = hasEventPartAssignment
      ? (eventPartLabel || '未設定')
      : (partMeta?.label || fallbackLabel || '未設定');
    const sectionShort = hasEventPartAssignment
      ? eventPartLabel
      : (partMeta?.short_name || fallbackShort || '');
    const sectionOrder = toMemberRowOrder(
      hasEventPartAssignment
        ? (artist?.event_part_sort_order ?? artist?.eventPartSortOrder)
        : (partMeta?.sort_order ?? partMeta?.order ?? artist?.part_sort_order ?? artist?.partSortOrder)
    );

    if (!sectionMap.has(sectionId)) {
      sectionMap.set(sectionId, {
        id: sectionId,
        title: sectionTitle,
        titleShort: sectionShort,
        order: sectionOrder,
        members: [],
      });
    }

    sectionMap.get(sectionId).members.push({
      id: artist?.artist_id || `group-artist-${index}`,
      name:
        pickFirstStringFn(
          artist?.name,
          artist?.artist_name,
          artist?.name_en,
          artist?.artist_name_en
        ) || '名称未設定',
      pic: artist?.artist_profile_picture || artist?.profile_picture || '',
      gender: artist?.artist_gender || artist?.gender || null,
      mainPart: hasEventPartAssignment
        ? eventPartLabel
        : (partMeta?.short_name || fallbackShort || ''),
      mainPartLabel: hasEventPartAssignment
        ? eventPartLabel
        : (partMeta?.label || fallbackLabel || ''),
      sortOrder: sectionOrder,
      isChief: !!(artist?.is_chief || artist?.isChief),
      isConcertmaster: !!(artist?.is_concertmaster || artist?.isConcertmaster),
      customRoles: Array.isArray(artist?.custom_roles)
        ? artist.custom_roles
        : (Array.isArray(artist?.customRoles) ? artist.customRoles : []),
      contractTs: (() => {
        const raw = artist?.contract_updated_at ?? artist?.contractUpdatedAt;
        const parsed = raw ? Date.parse(raw) : NaN;
        return Number.isFinite(parsed) ? parsed : null;
      })(),
      profile_one_liner: artist?.profile_one_liner || '',
      instagram_url: artist?.instagram_url || '',
      twitter_url: artist?.twitter_url || '',
      facebook_url: artist?.facebook_url || '',
      hp_url: artist?.hp_url || artist?.homepage_url || '',
      career_timeline: Array.isArray(artist?.career_timeline)
        ? artist.career_timeline
        : (Array.isArray(artist?.careerTimeline) ? artist.careerTimeline : []),
      eventMembershipLabel: '',
      eventPartLabel: hasEventPartAssignment ? eventPartLabel : '',
      eventUuids: [],
      eventPartAssignments: [],
      groupEvents: [],
    });
  });

  const sections = Array.from(sectionMap.values()).sort((a, b) => {
    const ao = toMemberRowOrder(a?.order);
    const bo = toMemberRowOrder(b?.order);
    if (ao !== bo) return ao - bo;
    return String(a?.title || '').localeCompare(String(b?.title || ''), 'ja');
  });
  sections.forEach((section) => {
    section.members.sort(compareEventMemberRows);
  });
  return sections;
};
